import { useCallback, useEffect, useState } from "react";
import { BellOff, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

/**
 * One master switch per person: pausing here stops EVERY outbound SMS to
 * that number (Health Check office alerts, booking notifications, anything
 * else), so there is no need to hunt across settings screens.
 */
export const StaffSmsPausePanel = () => {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/staff-sms-pause");
      setState(data);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = async (p) => {
    setBusy(p.mobile);
    try {
      const { data } = await api.post("/admin/staff-sms-pause", {
        mobile: p.mobile, label: p.name, paused: !p.paused,
      });
      setState(data);
      toast.success(p.paused ? `SMS resumed for ${p.name}` : `All SMS paused for ${p.name}`);
    } catch (e) { toast.error(e.response?.data?.detail || formatApiError(e)); }
    setBusy("");
  };

  if (!state) return null;

  return (
    <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4"
      data-testid="staff-sms-pause-panel">
      <div className="flex items-center gap-2">
        <BellOff className="h-4 w-4 text-slate-500" />
        <div className="text-[12px] font-extrabold uppercase tracking-wide text-slate-700">
          Pause all SMS to a person
        </div>
      </div>
      <p className="mt-1 max-w-2xl text-[12px] text-slate-600">
        One switch per person. Pausing stops every text to that number across
        the whole app — Health Check alerts, booking notifications, everything —
        without changing any other setting. Flip it back on any time.
      </p>
      {state.people.length === 0 ? (
        <p className="mt-3 text-[12px] text-slate-500" data-testid="staff-sms-pause-empty">
          No internal mobile numbers saved yet.
        </p>
      ) : (
        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          {state.people.map((p) => (
            <div key={p.mobile}
              data-testid={`staff-sms-pause-row-${p.mobile}`}
              className={`flex flex-wrap items-center justify-between gap-2 rounded-lg border p-2 ${p.paused
                ? "border-rose-200 bg-rose-50" : "border-slate-200"}`}>
              <div className="min-w-0">
                <div className="truncate text-[12px] font-bold text-slate-800">{p.name}</div>
                <div className="text-[11px] text-slate-500">
                  {p.mobile_masked}
                  {p.sources?.length ? ` · ${p.sources.join(", ")}` : ""}
                </div>
              </div>
              <button data-testid={`staff-sms-pause-toggle-${p.mobile}`}
                disabled={busy === p.mobile}
                onClick={() => toggle(p)}
                className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-[11px] font-bold border ${p.paused
                  ? "bg-rose-600 text-white border-rose-600"
                  : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"}`}>
                {busy === p.mobile && <Loader2 className="h-3 w-3 animate-spin" />}
                {p.paused ? "ALL SMS PAUSED" : "Pause all SMS"}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
